/**
 * Hury Store — Dark Mode Toggle + Animation Controller
 * Add via: <script src="{{ 'hury-dark-mode.js' | asset_url }}" defer></script>
 */

(function () {
  'use strict';

  const STORAGE_KEY = 'hury-theme';
  const DARK = 'dark';
  const LIGHT = 'light';

  /* ── 1. THEME PERSISTENCE ──────────────────────── */
  function getPreferredTheme() {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return stored;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? DARK : LIGHT;
  }

  function applyTheme(theme) {
    document.documentElement.setAttribute('data-hury-theme', theme);
    localStorage.setItem(STORAGE_KEY, theme);
    updateToggleLabel(theme);
  }

  function updateToggleLabel(theme) {
    const btn = document.getElementById('hury-theme-toggle');
    if (!btn) return;
    btn.setAttribute('aria-label', theme === DARK ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro');
    btn.setAttribute('aria-pressed', theme === DARK ? 'true' : 'false');
  }

  /* Apply immediately to avoid flash */
  applyTheme(getPreferredTheme());

  /* ── 2. INJECT TOGGLE BUTTON ───────────────────── */
  function injectToggle() {
    if (document.getElementById('hury-theme-toggle')) return;

    const btn = document.createElement('button');
    btn.id = 'hury-theme-toggle';
    btn.setAttribute('aria-label', 'Cambiar modo oscuro');
    btn.setAttribute('aria-pressed', 'false');
    btn.innerHTML = `
      <svg class="icon-moon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
      </svg>
      <svg class="icon-sun" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="5"/>
        <line x1="12" y1="1" x2="12" y2="3"/>
        <line x1="12" y1="21" x2="12" y2="23"/>
        <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
        <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
        <line x1="1" y1="12" x2="3" y2="12"/>
        <line x1="21" y1="12" x2="23" y2="12"/>
        <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
        <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
      </svg>`;

    btn.addEventListener('click', function () {
      const current = document.documentElement.getAttribute('data-hury-theme');
      const next = current === DARK ? LIGHT : DARK;
      applyTheme(next);

      /* Ripple animation on click */
      const ripple = document.createElement('span');
      ripple.style.cssText = `
        position:absolute; border-radius:50%; background:rgba(232,68,26,0.25);
        width:0; height:0; left:50%; top:50%; transform:translate(-50%,-50%);
        animation: hury-ripple 0.5s ease-out forwards; pointer-events:none;
      `;
      btn.appendChild(ripple);
      setTimeout(() => ripple.remove(), 600);
    });

    document.body.appendChild(btn);
    updateToggleLabel(getPreferredTheme());

    /* Inject ripple keyframe once */
    if (!document.getElementById('hury-ripple-style')) {
      const style = document.createElement('style');
      style.id = 'hury-ripple-style';
      style.textContent = `
        @keyframes hury-ripple {
          to { width: 80px; height: 80px; opacity: 0; }
        }
      `;
      document.head.appendChild(style);
    }
  }

  /* ── 3. SCROLL-TRIGGERED ANIMATIONS ───────────── */
  function initScrollAnimations() {
    if (!('IntersectionObserver' in window)) return;

    /* Footer fade-in */
    const footer = document.querySelector('.footer');
    if (footer) {
      const footerObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach(e => {
            if (e.isIntersecting) {
              e.target.classList.add('hury-visible');
              footerObserver.unobserve(e.target);
            }
          });
        },
        { threshold: 0.1 }
      );
      footerObserver.observe(footer);
    }

    /* Section headings slide-in */
    const headings = document.querySelectorAll('.title-wrapper, .collection-hero');
    const headingObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((e, i) => {
          if (e.isIntersecting) {
            setTimeout(() => {
              e.target.style.opacity = '1';
              e.target.style.transform = 'translateY(0)';
            }, i * 60);
            headingObserver.unobserve(e.target);
          }
        });
      },
      { threshold: 0.2, rootMargin: '0px 0px -40px 0px' }
    );
    headings.forEach(el => {
      el.style.opacity = '0';
      el.style.transform = 'translateY(20px)';
      el.style.transition = 'opacity 0.55s ease, transform 0.55s cubic-bezier(0.4,0,0.2,1)';
      headingObserver.observe(el);
    });

    /* Product / collection card stagger on scroll */
    const cards = document.querySelectorAll(
      '.product-card-wrapper, .collection-card-wrapper, .article-card-wrapper'
    );

    const cardObserver = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter(e => e.isIntersecting);
        visible.forEach((e, i) => {
          const el = e.target;
          el.style.animationDelay = `${i * 0.07}s`;
          el.classList.add('hury-in');
          cardObserver.unobserve(el);
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -30px 0px' }
    );

    cards.forEach(card => {
      card.style.opacity = '0';
      cardObserver.observe(card);
    });
  }

  /* ── 4. CART BUBBLE ANIMATION ─────────────────── */
  function watchCartBubble() {
    const bubble = document.querySelector('.cart-count-bubble');
    if (!bubble) return;

    const observer = new MutationObserver(() => {
      bubble.style.animation = 'none';
      bubble.offsetHeight; /* reflow */
      bubble.style.animation = 'hury-pop 0.3s cubic-bezier(0.34,1.56,0.64,1)';
    });
    observer.observe(bubble, { childList: true, subtree: true, characterData: true });
  }

  /* ── 5. IMAGE LOAD SHIMMER REMOVAL ────────────── */
  function initImageLoadHandlers() {
    document.querySelectorAll('.card__media img, .banner__media img').forEach(img => {
      const media = img.closest('.media');
      if (!media) return;
      if (img.complete) {
        media.classList.add('loaded');
      } else {
        img.addEventListener('load', () => media.classList.add('loaded'), { once: true });
      }
    });
  }

  /* ── 6. SMOOTH SCROLL BACK-TO-TOP ─────────────── */
  function injectBackToTop() {
    const btn = document.createElement('button');
    btn.id = 'hury-back-top';
    btn.setAttribute('aria-label', 'Volver arriba');
    btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="18 15 12 9 6 15"/></svg>`;
    btn.style.cssText = `
      position:fixed; bottom:7.2rem; right:2.4rem; z-index:9998;
      width:44px; height:44px; border-radius:50%;
      border:2px solid var(--hury-border, rgba(26,26,26,0.08));
      background:var(--hury-surface-card, #fff);
      color:var(--hury-ink, #1A1A1A);
      cursor:pointer; display:flex; align-items:center; justify-content:center;
      box-shadow:var(--hury-shadow-sm);
      opacity:0; pointer-events:none;
      transition:opacity 0.3s ease, transform 0.3s cubic-bezier(0.34,1.56,0.64,1),
                 background-color 0.35s ease;
    `;

    btn.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });

    btn.addEventListener('mouseenter', () => {
      btn.style.transform = 'scale(1.1) translateY(-2px)';
    });
    btn.addEventListener('mouseleave', () => {
      btn.style.transform = '';
    });

    document.body.appendChild(btn);

    window.addEventListener('scroll', () => {
      const show = window.scrollY > 400;
      btn.style.opacity = show ? '1' : '0';
      btn.style.pointerEvents = show ? 'auto' : 'none';
    }, { passive: true });
  }

  /* ── INIT ──────────────────────────────────────── */
  function init() {
    injectToggle();
    initScrollAnimations();
    watchCartBubble();
    initImageLoadHandlers();
    injectBackToTop();

    /* Listen for OS theme changes */
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
      if (!localStorage.getItem(STORAGE_KEY)) {
        applyTheme(e.matches ? DARK : LIGHT);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
